// Aligning source frames of the scans themselves (not of an object): each chosen frame's points are
// fitted onto everything else that is shown (the other frames, the other scans), and the fit becomes a
// tree.transform of that frame, so the scan is drawn and published with the frame moved. A computation
// like instops.ts: it returns the ops per scan and a note; the page commits them and runs the pool.
import { type Rigid, apply, compose, identity, inverse } from '../align.ts';
import type { State } from './log.ts';
import { type MarkVal, chainOf, frameTransform, markVal } from './log.ts';
import { type PoseGraph, type Tie, type TiltOpts, joinGraphs, relax, tieGap } from './springs.ts';
import type { V3 } from '../math.ts';
import type { OpBody } from './session.ts';
import { type FrameFit, centroid } from './instances.ts';
import type { Exec } from './instops.ts';

/** A loaded scan as frame alignment reads it: world copies (T · E(f) applied), frame ids, placement T. */
export interface FrameScan {
  scan: string; nv: number; P: Float32Array; N: Float32Array; frame: Uint32Array | null; state: State; T: Rigid;
  /** RTAB-Map's links between its frames (springs.ts), if the build has them. */
  links?: PoseGraph | null;
}

export interface FrameCtx {
  /** Per scan, the vertices that are shown (only they make the target and the frames). */
  scans: { s: FrameScan; live: Uint8Array; frames: Set<number> }[];
  exec: Exec;
  K: number;
  progress: (text: string, frac: number) => void;
  /** Springs: carry each aligned frame's unlocked neighbours along, fading over about this many links (0: off). */
  reach?: number;
}

/** Fit limits for a scan's own frames: a frame can be further off than an instance's, but past ~30 cm it is a wrong match. */
export const FRAME_FIT = { minPts: 150, maxShift: 0.3, maxYaw: 15 };
/** At most this many target points go to each worker (the target is voxel-thinned again there). */
const TARGET_CAP = 400_000;
/** Solve points may tilt a frame (pitch, roll) this far (degrees) from where RTAB-Map had it in its scan, and no further. */
export const POINT_TILT = { max: 3 };

/**
 * A world move W of a tree node's points as the node's new D: the node is drawn at T · P · D (T the scan's
 * placement, P the transform of what it sits in), so W · T · P · D = T · P · D' gives D' = (T·P)⁻¹ · W · (T·P) · D.
 */
export function worldToNodeD(T: Rigid, prefix: Rigid, D: Rigid, W: Rigid): Rigid {
  const TP = compose(T, prefix);
  return compose(inverse(TP), compose(W, compose(TP, D)));
}

/** A frame's new D for a world move W (frames in groups through their group's chain). */
export function frameD(s: Pick<FrameScan, 'state' | 'T'>, f: number, W: Rigid): Rigid {
  const t = s.state.tree;
  return worldToNodeD(s.T, chainOf(t, t.frameGroup.get(f) ?? null), t.frameD.get(f) ?? identity(), W);
}

export interface FrameOut {
  bodies: Map<string, OpBody[]>; note: string; fits: (FrameFit<string> & { cm: number })[];
  /** Per scan id, each moved frame's world move (fitted, or carried by the springs), for a preview. */
  moves: Map<string, Map<number, Rigid>>;
}

/** Fit each chosen frame onto the rest of what is shown; one tree.transform per scan with every frame that moved. */
export async function alignFrames(ctx: FrameCtx): Promise<FrameOut> {
  const empty = (note: string): FrameOut => ({ bodies: new Map(), note, fits: [], moves: new Map() });
  // locked frames are neither fitted (they stay where they are) nor part of the target's exclusion
  let nlocked = 0;
  const scans = ctx.scans.map(x => {
    const lk = x.s.state.tree.locked, frames = new Set([...x.frames].filter(f => !lk.has(f)));
    nlocked += x.frames.size - frames.size;
    return { ...x, frames };
  });
  ctx = { ...ctx, scans };
  if (!scans.some(x => x.frames.size)) return empty(nlocked ? `the selected frame${nlocked === 1 ? ' is' : 's are all'} locked: unlock to align` : 'nothing selected to align');
  // the target: every shown point with a normal, not in a chosen frame (every k-th, to at most TARGET_CAP)
  let nt = 0;
  for (const { s, live, frames } of ctx.scans) for (let v = 0; v < s.nv; v++)
    if (live[v] && s.N[3 * v] === s.N[3 * v] && !(s.frame && frames.has(s.frame[v]))) nt++;
  const step = Math.max(1, Math.ceil(nt / TARGET_CAP)), txyz = new Float32Array(3 * Math.ceil(nt / step)), tnrm = new Float32Array(txyz.length);
  let j = 0, k = 0;
  const list: { key: string; xyz: Float32Array }[] = [], where = new Map<string, { s: FrameScan; f: number }>();
  for (const { s, live, frames } of ctx.scans) {
    const pts = new Map<number, number[]>();
    for (let v = 0; v < s.nv; v++) {
      if (!live[v]) continue;
      const f = s.frame ? s.frame[v] : 0, i = 3 * v;
      if (f && frames.has(f)) { (pts.get(f) ?? pts.set(f, []).get(f)!).push(s.P[i], s.P[i + 1], s.P[i + 2]); continue; }
      if (s.N[i] !== s.N[i] || k++ % step) continue;
      txyz.set(s.P.subarray(i, i + 3), 3 * j); tnrm.set(s.N.subarray(i, i + 3), 3 * j); j++;
    }
    for (const [f, p] of [...pts].sort((a, b) => a[0] - b[0])) {
      const key = `${s.scan}|${f}`;
      list.push({ key, xyz: new Float32Array(p) }); where.set(key, { s, f });
    }
  }
  if (!list.length) return empty('none of the selected points are in a frame that is shown');
  const xyz = txyz.subarray(0, 3 * j), nrm = tnrm.subarray(0, 3 * j);
  // the frames split over the workers, each with its own copy of the target
  const parts = Array.from({ length: Math.min(ctx.K, list.length) }, (_, i) => list.filter((_, n) => n % ctx.K === i));
  let done = 0;
  const res = await Promise.all(parts.map(async l => {
    const r = await ctx.exec({ kind: 'fit', xyz: xyz.slice(), nrm: nrm.slice(), list: l, opts: FRAME_FIT }, true) as FrameFit<string>[] | null;
    done += l.length;
    ctx.progress(`${done} of ${list.length} frames`, done / list.length);
    return r;
  }));
  if (res.some(r => r === null)) return empty('too few shown points apart from the selected frames to fit them onto');
  const fits: FrameOut['fits'] = [], moves = new Map<FrameScan, Map<number, Rigid>>(), skip: Record<string, number> = {};
  const c0 = new Map(list.map(l => [l.key, centroid(l.xyz)]));
  for (const r of (res as FrameFit<string>[][]).flat()) {
    const c = c0.get(r.key)!, q = apply(r.W, c);
    fits.push({ ...r, cm: 100 * Math.hypot(q[0] - c[0], q[1] - c[1], q[2] - c[2]) });
    if (r.skip) { skip[r.skip] = (skip[r.skip] ?? 0) + 1; continue; }
    const { s, f } = where.get(r.key)!;
    (moves.get(s) ?? moves.set(s, new Map()).get(s)!).set(f, r.W);
  }
  // the springs: the fitted frames carry their neighbours along (locked ones stay), and named points still meet
  let carried = 0;
  const sp = springAll(ctx.scans.map(x => x.s), moves, ctx.reach ?? 0);
  for (const [s, m] of sp.moves) for (const [f, W] of m) { (moves.get(s) ?? moves.set(s, new Map()).get(s)!).set(f, W); carried++; }
  const bodies = new Map([...moves].map(([s, m]) => [s.scan, [{ op: 'tree.transform', D: Object.fromEntries([...m].map(([f, W]) => [`f:${f}`, frameD(s, f, W)])) } as OpBody]]));
  const moved = fits.filter(f => !f.skip), cm = moved.map(f => f.cm).sort((a, b) => a - b);
  const rms = (a: number) => (a * 100).toFixed(1);
  const note = `${moved.length} of ${fits.length} frame${fits.length === 1 ? '' : 's'} aligned` + (carried ? `, ${carried} carried along by the springs` : '') + tiesNote(sp.ties) + (nlocked ? `, ${nlocked} locked left alone` : '') +
    (moved.length === 1 ? ` (moved ${cm[0].toFixed(1)} cm, rms ${rms(moved[0].rms0)} → ${rms(moved[0].rms1)} cm)` : cm.length ? ` (median move ${cm[cm.length >> 1].toFixed(1)} cm)` : '') +
    (fits.length > moved.length ? `; left alone: ${Object.entries(skip).map(([w, n]) => `${n} ${w === 'few points' ? `with < ${FRAME_FIT.minPts} points` : w === 'too far' ? `> ${FRAME_FIT.maxShift * 100} cm / ${FRAME_FIT.maxYaw}°` : w}`).join(', ')}` : '');
  return { bodies, note, fits, moves: new Map([...moves].map(([s, m]) => [s.scan, m])) };
}

/**
 * Named points: per name, the frames of the loaded scans that have it, with the point in the world now (T · E(f) · p),
 * its own weight w and the name's weight nw in its scan (the two multiply).
 */
export function tiesOf<S extends Pick<FrameScan, 'state' | 'T'>>(scans: S[]) {
  const by = new Map<string, { s: S; f: number; p: V3; o: V3; w: number; nw: number; q: V3 }[]>();
  for (const s of scans) for (const [name, m] of s.state.tree.marks) for (const [f, { p, o, w }] of m)
    (by.get(name) ?? by.set(name, []).get(name)!).push({ s, f, p, o, w, nw: s.state.tree.markW.get(name) ?? 1, q: apply(compose(s.T, frameTransform(s.state.tree, f)), p) });
  return new Map([...by].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })));
}

export interface TieOut { name: string; n: number; before: number; after: number; held: boolean }

/**
 * The springs over every scan at once (springs.ts relax on their pose graphs joined, so a named point can tie
 * frames of different scans): anchors are the frames already moved (world W, per scan), each scan's locked
 * frames stay, and frames that share a named point are pulled together until those points meet. A frame
 * with no pose in the graph (no links) turns about its named point. tiltMax (degrees, 0: yaw only): frames may also
 * pitch and roll, up to that far from where RTAB-Map had them in their scan (T · scanD · pose). Returns the other
 * frames' world moves, per scan, how far apart each named point's members were and are, and how many frames
 * were held at tiltMax.
 */
export function springAll(scans: FrameScan[], anchors: Map<FrameScan, Map<number, Rigid>>, reach: number, tiltMax = 0) {
  const list = [...tiesOf(scans)].filter(([, ms]) => ms.length > 1), at = new Map<FrameScan, Map<number, V3>>();
  for (const [, ms] of list) for (const m of ms) (at.get(m.s) ?? at.set(m.s, new Map()).get(m.s)!).set(m.f, m.p);
  const use = scans.filter(s => (anchors.get(s)?.size && s.links?.links.length && reach > 0) || at.has(s));
  const J = joinGraphs(use.map(s => {
    const t = s.state.tree, mk = at.get(s);
    const pose = (f: number): Rigid => s.links?.pose.get(f) ?? (mk?.has(f) ? { R: identity().R, t: mk.get(f)! } : identity());
    return { key: s, g: s.links ?? null, place: (f: number) => compose(s.T, compose(frameTransform(t, f), pose(f))), frames: mk?.keys() };
  }));
  const A = new Map<number, Rigid>(), locked = new Set<number>();
  for (const s of use) {
    for (const [f, W] of anchors.get(s) ?? []) { const i = J.id(s, f); if (i !== undefined) A.set(i, W); }
    for (const f of s.state.tree.locked) { const i = J.id(s, f); if (i !== undefined) locked.add(i); }
  }
  const ties: Tie[] = list.map(([, ms]) => ms.map(m => ({ f: J.id(m.s, m.f)!, q: m.q, o: m.o, w: m.w * m.nw })));
  // the editor's turn on a frame on top of RTAB-Map's, in the world: (T·scanD) · (groups · D) · (T·scanD)⁻¹
  const tilt: TiltOpts | undefined = tiltMax > 0 ? {
    max: (tiltMax * Math.PI) / 180, capped: new Set(),
    edited: i => { const { key: s, f } = J.of(i), t = s.state.tree, TS = compose(s.T, t.scanD); return compose(TS, compose(inverse(t.scanD), compose(frameTransform(t, f), inverse(TS)))); },
  } : undefined;
  const out = relax(J.g, J.place, A, locked, reach, ties, tilt), moves = new Map<FrameScan, Map<number, Rigid>>();
  for (const [i, W] of out) { const { key: s, f } = J.of(i); (moves.get(s) ?? moves.set(s, new Map()).get(s)!).set(f, W); }
  const moved = (i: number) => A.get(i) ?? out.get(i);
  return {
    moves, capped: tilt?.capped?.size ?? 0,
    ties: list.map(([name], k): TieOut => ({ name, n: ties[k].length, before: tieGap(ties[k]), after: tieGap(ties[k], moved), held: ties[k].every(m => locked.has(m.f) || A.has(m.f) || !m.w) })),
  };
}

const cmOf = (m: number) => (100 * m).toFixed(1);
/** ", 2 named points met (widest gap 4.2 → 0.0 cm)"; and the ones that cannot be (ties that were met already: nothing). */
function tiesNote(ties: TieOut[]) {
  const met = ties.filter(t => !t.held && t.before > 1e-4), held = ties.filter(t => t.held && t.after > 1e-4);
  return (met.length ? `, ${met.length} named point${met.length === 1 ? '' : 's'} met (widest gap ${cmOf(Math.max(...met.map(t => t.before)))} → ${cmOf(Math.max(...met.map(t => t.after)))} cm)` : '') +
    (held.length ? `; ${held.map(t => `"${t.name}"`).join(', ')} held: every frame of ${held.length === 1 ? 'it' : 'them'} is locked or aligned` : '');
}

/** Solve points: pull together the frames that share a named point (no fitting; they may turn, and tilt up to POINT_TILT), the springs carrying their neighbours along. */
export function solvePoints(scans: FrameScan[], reach: number): FrameOut {
  const sp = springAll(scans, new Map(), reach, POINT_TILT.max), nf = [...sp.moves.values()].reduce((a, m) => a + m.size, 0);
  const bodies = new Map([...sp.moves].map(([s, m]) => [s.scan, [{ op: 'tree.transform', D: Object.fromEntries([...m].map(([f, W]) => [`f:${f}`, frameD(s, f, W)])) } as OpBody]]));
  const note = !sp.ties.length ? 'no named point is in two frames: name the same point in another frame first'
    : !nf && !sp.ties.some(t => t.after > 1e-4) ? 'the named points meet already'
    : `${nf} frame${nf === 1 ? '' : 's'} moved` + tiesNote(sp.ties) +
      (sp.capped ? `; ${sp.capped} held at the ${POINT_TILT.max}° tilt limit (from where RTAB-Map had ${sp.capped === 1 ? 'it' : 'them'})` : '');
  return { bodies, note, fits: [], moves: new Map([...sp.moves].map(([s, m]) => [s.scan, m])) };
}

/**
 * Name a point: in every frame with selected points (that are shown), the centroid of those points, kept in
 * the frame's own coordinates so it moves with the frame. One tree.mark per scan, replacing the name's point
 * in those frames (keeping its offset and weight), and setting the name's weight nw where a scan has another.
 */
export function markOps(scans: { s: FrameScan; sel: Uint8Array; live: Uint8Array }[], name: string, nw = 1) {
  const bodies = new Map<string, OpBody[]>();
  let n = 0;
  for (const { s, sel, live } of scans) {
    if (!s.frame) continue;
    const sum = new Map<number, number[]>();
    for (let v = 0; v < s.nv; v++) {
      const f = s.frame[v];
      if (!sel[v] || !live[v] || !f) continue;
      const a = sum.get(f) ?? sum.set(f, [0, 0, 0, 0]).get(f)!;
      a[0] += s.P[3 * v]; a[1] += s.P[3 * v + 1]; a[2] += s.P[3 * v + 2]; a[3]++;
    }
    if (!sum.size) continue;
    const p: Record<string, MarkVal> = {}, old = s.state.tree.marks.get(name);
    for (const [f, a] of [...sum].sort((x, y) => x[0] - y[0])) {
      const q = apply(inverse(compose(s.T, frameTransform(s.state.tree, f))), [a[0] / a[3], a[1] / a[3], a[2] / a[3]]);
      p[`f:${f}`] = markVal({ o: [0, 0, 0], w: 1, ...old?.get(f), p: q.map(x => Math.round(x * 1e6) / 1e6) as V3 });
    }
    n += sum.size;
    bodies.set(s.scan, [{ op: 'tree.mark', name, p, ...((s.state.tree.markW.get(name) ?? 1) !== nw ? { w: nw } : {}) } as OpBody]);
  }
  return { bodies, n };
}
